import React, { useState, useEffect } from "react";
import { Button } from "@clarkinc/zest-design-system/ui";
import { allRatings, allVariations, sortReviews } from "./CustomerReviewHelper";
import { CustomerReviews, ProductReviewDetail } from "./reviewModels";
import {
  sendGtmEvent,
  clearGa4EventInDataLayer,
} from "../../analytics/definitions";
import MultipleVariationFilter from "./MultipleVariationFilter";
import RatingsFilter, { Variation } from "./RatingsFilter";
import { useFeatureFlag } from "../contexts/FeatureFlagProvider";

interface ReviewAggregationToolbarProps {
  reviews: CustomerReviews;
  reviewLink: string;
  isProductReviewPage?: boolean;
  allProductReviews: readonly ProductReviewDetail[];
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  setProductReviewsToDisplay: React.Dispatch<
    React.SetStateAction<readonly ProductReviewDetail[]>
  >;
}

export interface FilteredBy {
  variation: Variation;
  rating: string | undefined;
}

const ReviewAggregationToolbar = ({
  reviews,
  reviewLink,
  isProductReviewPage = false,
  allProductReviews,
  setCurrentPage,
  setProductReviewsToDisplay,
}: ReviewAggregationToolbarProps) => {
  const defaultReviewSortBy = isProductReviewPage ? "date" : "most_helpful";
  const [reviewSortBy, setReviewSortBy] = useState(defaultReviewSortBy);
  const { evaluateFlag } = useFeatureFlag();
  const filterByRatingEnabled = evaluateFlag("filter-by-rating");
  const defaultFilteredBy = {
    rating: allRatings,
    variation: { type: undefined, value: allVariations },
  };
  const [filteredBy, setFilteredBy] = useState<FilteredBy>(defaultFilteredBy);

  const [filteredReviewIds, setFilteredReviewIds] = useState<number[]>(
    [...allProductReviews].map((review) => review.reviewId),
  );

  const totalReviews = allProductReviews.length;

  const sortChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setReviewSortBy(event.target.value);

    clearGa4EventInDataLayer();
    sendGtmEvent({
      event: "ga4_event",
      eventName: "Customer Reviews - Sort By Dropdown Change",
      eventLabel: `Sort By: ${event.target.value}`,
    });
  };

  const filterByChangeHandler = (
    filteredReviewIds: number[],
    gtmLabel?: string,
  ): void => {
    setFilteredReviewIds(filteredReviewIds);

    if (gtmLabel) {
      clearGa4EventInDataLayer();
      sendGtmEvent({
        event: "ga4_event",
        eventName: "Customer Reviews - Filter By Dropdown Change",
        eventLabel: `Filter By: ${gtmLabel}`,
      });
    }

    setCurrentPage(1);
  };

  const onVariationFilterChanged = (variation: Variation) => {
    setFilteredBy({ variation, rating: filteredBy.rating });
  };

  const onRatingFilterChanged = (rating: string | undefined) => {
    setFilteredBy({ rating, variation: filteredBy.variation });
  };

  useEffect(() => {
    const sortedReviews = sortReviews([...allProductReviews], reviewSortBy);
    const sortedAndFilteredReviews = sortedReviews.filter((review) =>
      filteredReviewIds.includes(review.reviewId),
    );

    setProductReviewsToDisplay(sortedAndFilteredReviews);
  }, [
    filteredReviewIds,
    setProductReviewsToDisplay,
    allProductReviews,
    reviewSortBy,
  ]);

  return (
    <div className="review-aggregation-toolbar">
      {totalReviews > 0 && (
        <>
          <div className="float-left mr-3.5 mt-[7px] xs:mt-0">
            <label className="review-aggregation__label block" htmlFor="sortby">
              Sort By
            </label>
            <select
              className="suffix_dropdown configure item_number text-sm w-[195px] xxxs:w-[155px] xxs:w-[165px] xsl:w-[195px]"
              id="sortby"
              name="SortBy"
              onChange={sortChangeHandler}
              data-testid="review-sort-dropdown"
              defaultValue={isProductReviewPage ? "date" : undefined}
            >
              <option value="most_helpful">Most Helpful</option>
              <option value="highest_rated">Highest Rating</option>
              <option value="lowest_rated">Lowest Rating</option>
              <option value="date">Date</option>
            </select>
          </div>
          <MultipleVariationFilter
            onVariationTypeChange={filterByChangeHandler}
            allProductReviews={allProductReviews}
            variationOptions={reviews.variationOptions}
            filteredBy={filteredBy}
            onVariationFilterChanged={onVariationFilterChanged}
          />
          {filterByRatingEnabled && (
            <RatingsFilter
              onVariationTypeChange={filterByChangeHandler}
              allProductReviews={allProductReviews}
              filteredBy={filteredBy}
              onRatingFilterChanged={onRatingFilterChanged}
            />
          )}
          {filterByRatingEnabled &&
            filteredBy.rating !== "all" &&
            filteredBy.variation.type && (
              <div className="float-left pl-3.5 sm:mt-6">
                <Button
                  variant="plain"
                  theme="wss"
                  size="medium"
                  onClick={() => {
                    setFilteredBy(defaultFilteredBy);

                    filterByChangeHandler(
                      allProductReviews.map((r) => r.reviewId),
                    );
                  }}
                  data-testid="clear-filters"
                >
                  <span>Clear Filters</span>
                </Button>
              </div>
            )}
          {totalReviews >= 40 && reviewLink.length > 0 && (
            <div className="float-left sm:float-right w-full sm:w-auto sm:mt-6">
              <a href={reviewLink} data-testid="see-all-reviews-link">
                <span>{`See All ${totalReviews} Reviews`}</span>
              </a>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ReviewAggregationToolbar;
